<template>
  <div class="material-detail-page" :class="{ active: state.materialDetailOpen }">
    <div class="material-detail-close" @click="closeMaterialDetail">
      <i class="material-detail-close__icon fa-solid fa-xmark"></i>
    </div>
    <div class="material-detail__main">
      <div v-if="state.detailVideo" class="material-detail__layout">
        <!-- 左侧视频区 -->
        <div class="material-detail__media">
          <div class="material-detail__player">
            <template v-if="activeMedia?.type === 'video'">
              <div v-if="!videoLoaded" class="material-detail__player-loading">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>视频加载中</span>
              </div>
              <video
                ref="videoEl"
                :src="activeMedia.src"
                class="material-detail__player-el"
                :class="{ 'material-detail__player-el--hidden': !videoLoaded }"
                controls
                autoplay
                loop
                @loadedmetadata="videoLoaded = true"
              ></video>
            </template>
            <img
              v-else-if="activeMedia?.type === 'image'"
              :src="activeMedia.src"
              class="material-detail__player-el"
            />
          </div>
          <div v-if="state.detailVideo.media?.length" class="material-detail__thumbs">
            <button
              v-for="(m, i) in state.detailVideo.media"
              :key="`${m.type}-${m.src}-${i}`"
              type="button"
              class="material-detail__thumb"
              :class="{ 'material-detail__thumb--active': i === activeMediaIndex }"
              @click="selectMedia(i)"
            >
              <img v-if="m.type === 'image'" :src="m.src" class="material-detail__thumb-img" />
              <div v-else class="material-detail__thumb-video">
                <i class="material-detail__thumb-video-icon fa-solid fa-play"></i>视频
              </div>
            </button>
          </div>
          <div class="material-detail__meta-row">
            <div class="material-detail__author">
              <div class="material-detail__avatar">
                {{ state.detailVideo.avatar }}
              </div>
              <div>
                <p class="material-detail__author-name">{{ state.detailVideo.author }}</p>
              </div>
            </div>
            <span class="material-detail__duration">{{ state.detailVideo.duration }}</span>
          </div>
        </div>

        <!-- 右侧信息区 -->
        <div class="material-detail__sidebar">
          <div class="prompt-card material-detail__prompt-card">
            <h3 class="material-detail__recipe-title">
              <i class="material-detail__recipe-title-icon fa-solid fa-wand-magic-sparkles"></i>生成配方
            </h3>
            <p class="material-detail__recipe-desc">使用此 Prompt 即可一键生成同款视频</p>
            <div class="prompt-text-area material-detail__prompt-text">{{ state.detailVideo.prompt }}</div>
            <button disabled class="make-same-btn material-detail__make-same material-detail__make-same--sunset" title="Beta 试用已结束，视频生成功能已暂停">
              做同款
            </button>
          </div>
          <div class="material-detail__actions">
            <button @click="copyPrompt" class="material-detail__action material-detail__action--copy">
              <i class="material-detail__action-icon fa-regular fa-copy"></i>{{ copied ? '已复制' : '复制提示词' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAppStore } from '../composables/useAppStore'
import { reportClick } from '../util/report'

const { state, closeMaterialDetail } = useAppStore()

const videoEl = ref<HTMLVideoElement | null>(null)
const videoLoaded = ref(false)
const copied = ref(false)
const liked = ref(false)
const activeMediaIndex = ref(0)
const activeMedia = computed(() => state.detailVideo?.media?.[activeMediaIndex.value] || null)

watch(() => state.materialDetailOpen, (val) => {
  if (!val && videoEl.value) {
    videoEl.value.pause()
    videoEl.value.src = ''
  }
  if (val) {
    copied.value = false
    liked.value = false
    activeMediaIndex.value = 0
    videoLoaded.value = false
  }
})

watch(activeMedia, () => {
  videoLoaded.value = false
  if (videoEl.value && activeMedia.value?.type === 'video') {
    videoEl.value.load()
  }
})

function selectMedia(index: number) {
  activeMediaIndex.value = index
}

function copyPrompt() {
  if (!state.detailVideo) return
  reportClick('index', 'prompt_copy')
  navigator.clipboard.writeText(state.detailVideo.prompt).then(() => {
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  })
}
</script>

<style scoped>
.material-detail-close__icon {
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #6b7280;
}

.material-detail__main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.material-detail__layout {
  width: 100%;
  max-width: 72rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2.5rem;
}

@media (min-width: 1024px) {
  .material-detail__layout {
    flex-direction: row;
  }
}

.material-detail__media {
  flex: 1;
  min-width: 0;
}

.material-detail__player {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 1.5rem;
  background-color: #f3f4f6;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
}

.material-detail__player-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #f3f4f6;
  border-radius: inherit;
  color: #9ca3af;
  font-size: 14px;
  z-index: 1;
}

.material-detail__player-loading i {
  font-size: 24px;
}

.material-detail__player-el {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.material-detail__player-el--hidden {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.material-detail__thumbs {
  margin-top: 1rem;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
}

.material-detail__thumb {
  overflow: hidden;
  border-radius: 0.5rem;
  border-width: 1px;
  border-style: solid;
  border-color: #e5e7eb;
  background-color: rgb(243 244 246 / 0.6);
  padding: 0;
  cursor: pointer;
}

.material-detail__thumb--active {
  border-color: #3b82f6;
}

.material-detail__thumb-img {
  width: 100%;
  height: 4rem;
  object-fit: cover;
  display: block;
}

.material-detail__thumb-video {
  width: 100%;
  height: 4rem;
  background-color: #000;
  color: #fff;
  font-size: 0.75rem;
  line-height: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-detail__thumb-video-icon {
  margin-right: 0.25rem;
}

.material-detail__meta-row {
  margin-top: 1rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.material-detail__author {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.material-detail__avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: #dbeafe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 700;
}

.material-detail__author-name {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.material-detail__source {
  font-size: 11px;
  color: #9ca3af;
}

.material-detail__duration {
  font-size: 0.75rem;
  line-height: 1rem;
  color: #6b7280;
  background-color: #f3f4f6;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
}

.material-detail__sidebar {
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

@media (min-width: 1024px) {
  .material-detail__sidebar {
    width: 380px;
  }
}

.material-detail__prompt-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.material-detail__recipe-title {
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.material-detail__recipe-title-icon {
  color: #3b82f6;
  margin-right: 0.5rem;
}

.material-detail__recipe-desc {
  font-size: 0.75rem;
  line-height: 1rem;
  color: #6b7280;
  margin-bottom: 1.25rem;
}

.material-detail__prompt-text {
  flex: 1;
  margin-bottom: 1.5rem;
  white-space: pre-wrap;
}

.material-detail__make-same {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-detail__actions {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #6b7280;
}

.material-detail__action {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
  display: flex;
  align-items: center;
  transition: color 0.15s ease;
}

.material-detail__action--copy:hover {
  color: #2563eb;
}

.material-detail__action--like:not(.material-detail__action--liked):hover {
  color: #ef4444;
}

.material-detail__action--liked {
  color: #ef4444;
}

.material-detail__action-icon {
  margin-right: 0.375rem;
}

/* ===== Beta 下线禁用 — REQ-SUNSET-002 ===== */
.material-detail__make-same--sunset {
  background: #e5e7eb !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  pointer-events: none;
}
</style>
